import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import type { PluginListenerHandle } from '@capacitor/core'
const NATIVE_OAUTH_CALLBACK_PREFIX = 'ascendcomms://auth/callback'

type AuthSessionResult =
  | { type: 'success'; url: string }
  | { type: 'canceled' }
  | { type: 'error'; error: unknown }

let activeListener: PluginListenerHandle | null = null
let resolveOAuth: ((result: AuthSessionResult) => void) | null = null

export function isNativeOAuthCallbackUrl(url: string): boolean {
  return url.startsWith(NATIVE_OAUTH_CALLBACK_PREFIX)
}

async function clearActiveListener(): Promise<void> {
  if (activeListener) {
    await activeListener.remove()
    activeListener = null
  }
}

async function handleOAuthCallbackUrl(url: string): Promise<void> {
  if (!isNativeOAuthCallbackUrl(url)) return
  await Browser.close().catch(() => {})
  resolveOAuth?.({ type: 'success', url })
  resolveOAuth = null
}

/** Amplify authSessionOpener — Cognito Hosted UI in an in-app browser tab. */
export async function nativeAuthSessionOpener(
  url: string,
  _redirectUrls: string[],
): Promise<AuthSessionResult> {
  await clearActiveListener()

  return new Promise((resolve) => {
    resolveOAuth = resolve

    void Browser.open({ url }).catch((error) => {
      resolveOAuth = null
      void clearActiveListener()
      resolve({ type: 'error', error })
    })
  })
}

/** Register deep-link handlers once at startup (warm resume + cold start). */
export function registerNativeOAuthHandlers(): void {
  if (activeListener) return

  void App.addListener('appUrlOpen', (event) => {
    void handleOAuthCallbackUrl(event.url)
  }).then((handle) => {
    activeListener = handle
  })

  void App.getLaunchUrl().then((launch) => {
    if (launch?.url) {
      void handleOAuthCallbackUrl(launch.url)
    }
  })
}
